import type { PlatformBridge, PlatformCapabilities } from './contracts';
import { downloadBlob } from './image-engine';
import { takeSharedFiles } from './share-intake';

interface NativeIncomingFile {
  path: string;
  name: string;
  mimeType: string;
}

interface DesktopIncomingFile {
  name: string;
  mimeType: string;
  bytes: number[];
}

function isNative(): boolean {
  return '__TAURI_INTERNALS__' in window;
}

class BrowserPlatformBridge implements PlatformBridge {
  capabilities(): PlatformCapabilities {
    return {
      native: isNative(),
      shareFiles: Boolean(navigator.share),
      receiveSharedFiles: 'serviceWorker' in navigator
    };
  }

  async pickImages(multiple = true): Promise<File[]> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = multiple;
      input.accept = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';
      input.addEventListener('change', () => resolve(Array.from(input.files ?? [])), { once: true });
      input.click();
    });
  }

  async saveFile(blob: Blob, suggestedName: string): Promise<void> {
    downloadBlob(blob, suggestedName);
  }

  async share(files: File[], title?: string): Promise<boolean> {
    if (!navigator.share || !navigator.canShare?.({ files })) return false;
    await navigator.share({ files, title });
    return true;
  }

  async receiveExternalFiles(): Promise<File[]> {
    const browserFiles = await takeSharedFiles();
    if (!isNative()) return browserFiles;
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const desktopFiles = await invoke<DesktopIncomingFile[]>('take_desktop_files');
      const response = await invoke<{ files: NativeIncomingFile[] }>('plugin:realimager-share|take_pending_images');
      const nativeFiles = await Promise.all(response.files.map(async (item) => {
        const bytes = await invoke<number[]>('read_received_file', { path: item.path });
        return new File([new Uint8Array(bytes)], item.name, { type: item.mimeType });
      }));
      return [
        ...browserFiles,
        ...desktopFiles.map((item) => new File([new Uint8Array(item.bytes)], item.name, { type: item.mimeType })),
        ...nativeFiles
      ];
    } catch {
      return browserFiles;
    }
  }
}

export const platformBridge: PlatformBridge = new BrowserPlatformBridge();
